import {parseSketchPersistentDocument} from '../../sketch/persistence/parseSketchPersistentDocument';
import type {SketchPersistentDocument} from '../../sketch/persistence/SketchPersistenceTypes';

//------ 离线包草图扩展 开始------
/**
 * assembly.json.extensions 中保存草图数据的字段名。
 */
export const PUPPY_SKETCH_EXTENSION_KEY = 'sketches';

/**
 * 离线包草图扩展 schema 版本。
 */
export enum PuppySketchExtensionSchemaVersion {
    /** 第一版：点/直线/圆/圆弧 + 核心几何关系。 */
    V1 = 'puppy-sketch-extension-v1',
}

/**
 * 离线包草图扩展单条记录。
 *
 * 设计说明：
 * 1. `sketch` 是领域层定义文档（纯定义事实）；
 * 2. `displayState` 是文档级展示事实（被特征吸收的草图默认隐藏），属于离线包还原语义，
 *    不属于草图定义，因此放在信封层而不进入领域 schema；编辑态在保存时归一为 `shown`；
 * 3. `order/createdAt` 是设计树与特征时间线的文档级位置事实，同样由信封层持久化，
 *    避免 `sketchList` 为保证字节确定性按 sketchId 排序后丢失用户看到的草图顺序。
 */
export interface PuppySketchExtensionEntry {
    /** 草图展示状态。 */
    readonly displayState: 'shown' | 'hidden';
    /** 草图在设计树中的稳定顺序；旧版离线包允许缺省。 */
    readonly order?: number;
    /** 草图创建时间戳，用于恢复其在特征时间线中的位置；旧版离线包允许缺省。 */
    readonly createdAt?: number;
    /** 草图持久化文档。 */
    readonly sketch: SketchPersistentDocument;
}

/**
 * 离线包草图扩展载荷。
 *
 * 设计说明：
 * 1. 扩展只是薄信封：版本 + 草图记录列表；单个草图文档的结构与校验
 *    委托给领域层 `parseSketchPersistentDocument`（与线性阵列扩展同范式）；
 * 2. 确定性约束：sketchList 顺序由写入方按 sketchId 字典序固定；
 * 3. `createdAt` 是草图首次创建后保持稳定的文档事实，不是保存时生成的随机时间，
 *    因此持久化该字段仍能保证“场景无变化 → 连续保存字节一致”。
 */
export interface PuppySketchExtension {
    /** 扩展 schema 版本。 */
    readonly schemaVersion: PuppySketchExtensionSchemaVersion;
    /** 草图记录列表（按 sketchId 字典序）。 */
    readonly sketchList: readonly PuppySketchExtensionEntry[];
}

/**
 * 判断未知值是否为普通记录对象。
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 校验单条草图记录的展示状态。
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 合法展示状态。
 */
function parseSketchDisplayState(value: unknown, path: string): 'shown' | 'hidden' {
    if (value !== 'shown' && value !== 'hidden') {
        throw new Error(`PuppyCAD 离线包草图扩展展示状态无效: ${path}=${String(value)}`);
    }
    return value;
}

/**
 * 校验可选的草图设计树顺序。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 合法的非负整数；字段缺省时返回 `undefined`。
 */
function parseOptionalSketchOrder(value: unknown, path: string): number | undefined {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw new Error(`PuppyCAD 离线包草图扩展顺序无效: ${path}=${String(value)}`);
    }
    return value;
}

/**
 * 校验可选的草图创建时间戳。
 *
 * @param value 未知值。
 * @param path 字段路径。
 * @returns 合法的有限非负时间戳；字段缺省时返回 `undefined`。
 */
function parseOptionalSketchCreatedAt(value: unknown, path: string): number | undefined {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error(`PuppyCAD 离线包草图扩展创建时间无效: ${path}=${String(value)}`);
    }
    return value;
}

/**
 * 根据草图记录列表创建离线包草图扩展。
 * @param entries 草图记录列表。
 * @returns 有草图数据时返回扩展；没有数据时返回 `undefined`。
 */
export function createPuppySketchExtension(
    entries: readonly PuppySketchExtensionEntry[] | null | undefined,
): PuppySketchExtension | undefined {
    if (!entries || entries.length === 0) {
        return undefined;
    }

    /**
     * 写入前逐份重新校验并按 sketchId 字典序排序，保证扩展内容自洽且确定。
     */
    const sketchList = entries
        .map((entry, index) => ({
            displayState: parseSketchDisplayState(entry.displayState, `sketchList[${index}].displayState`),
            order: parseOptionalSketchOrder(entry.order, `sketchList[${index}].order`),
            createdAt: parseOptionalSketchCreatedAt(entry.createdAt, `sketchList[${index}].createdAt`),
            sketch: parseSketchPersistentDocument(entry.sketch),
        }))
        .sort((left, right) => left.sketch.sketchId.localeCompare(right.sketch.sketchId));
    assertUniqueSketchIds(sketchList);

    return {
        schemaVersion: PuppySketchExtensionSchemaVersion.V1,
        sketchList,
    };
}

/**
 * 解析 `.puppy` 草图扩展。
 * @param value 未知 JSON 值。
 * @returns 已校验的草图扩展。
 * @throws 当输入不是对象、扩展版本不受支持或 `sketchList` 不是数组时。
 * @throws 当任一草图记录不是对象或记录内容无法解析时，抛出包含记录索引的错误。
 */
export function parsePuppySketchExtension(value: unknown): PuppySketchExtension {
    if (!isRecord(value)) {
        throw new Error('PuppyCAD 离线包草图扩展必须是对象');
    }
    if (value.schemaVersion !== PuppySketchExtensionSchemaVersion.V1) {
        throw new Error(`不支持的 PuppyCAD 离线包草图扩展版本: ${String(value.schemaVersion)}`);
    }
    if (!Array.isArray(value.sketchList)) {
        throw new Error('PuppyCAD 离线包草图扩展字段必须是数组: sketchList');
    }

    const sketchList = value.sketchList.map((item, index) => {
        if (!isRecord(item)) {
            throw new Error(`PuppyCAD 离线包草图扩展 sketchList[${index}] 必须是对象`);
        }
        try {
            return {
                displayState: parseSketchDisplayState(item.displayState, `sketchList[${index}].displayState`),
                order: parseOptionalSketchOrder(item.order, `sketchList[${index}].order`),
                createdAt: parseOptionalSketchCreatedAt(item.createdAt, `sketchList[${index}].createdAt`),
                sketch: parseSketchPersistentDocument(item.sketch),
            };
        } catch (error) {
            throw new Error(`PuppyCAD 离线包草图扩展 sketchList[${index}] 非法: ${String(error instanceof Error ? error.message : error)}`);
        }
    });
    assertUniqueSketchIds(sketchList);

    return {
        schemaVersion: PuppySketchExtensionSchemaVersion.V1,
        sketchList,
    };
}

/**
 * 断言草图 ID 全局唯一。
 * @param sketchList 草图记录列表。
 */
function assertUniqueSketchIds(sketchList: readonly PuppySketchExtensionEntry[]): void {
    const seen = new Set<string>();
    sketchList.forEach((entry) => {
        if (seen.has(entry.sketch.sketchId)) {
            throw new Error(`PuppyCAD 离线包草图扩展存在重复 sketchId: ${entry.sketch.sketchId}`);
        }
        seen.add(entry.sketch.sketchId);
    });
}

/**
 * 从 assembly.json extensions 中读取草图扩展。
 * @param extensions assembly.json.extensions。
 * @returns 存在时返回草图扩展；不存在时返回 `undefined`。
 */
export function readPuppySketchExtensionFromAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
): PuppySketchExtension | undefined {
    const value = extensions?.[PUPPY_SKETCH_EXTENSION_KEY];
    return value === undefined ? undefined : parsePuppySketchExtension(value);
}

/**
 * 把草图扩展写入 assembly.json extensions。
 * @param extensions 原始扩展字段。
 * @param sketchExtension 草图扩展。
 * @returns 写入后的扩展字段；没有草图数据时返回原扩展副本。
 */
export function writePuppySketchExtensionToAssemblyExtensions(
    extensions: Readonly<Record<string, unknown>> | undefined,
    sketchExtension: PuppySketchExtension | undefined,
): Record<string, unknown> | undefined {
    const nextExtensions = extensions ? {...extensions} : {};
    if (sketchExtension) {
        nextExtensions[PUPPY_SKETCH_EXTENSION_KEY] = sketchExtension;
    } else {
        delete nextExtensions[PUPPY_SKETCH_EXTENSION_KEY];
    }
    return Object.keys(nextExtensions).length > 0 ? nextExtensions : undefined;
}
//------ 离线包草图扩展 结束------
